import * as pc from 'playcanvas';

import type { HdriConfig } from './scene-types';

/**
 * Load an equirectangular HDRI (PNG, JPG, or HDR) and install it as the
 * scene's env-atlas (IBL — drives reflections and ambient lighting on PBR
 * meshes). Optionally also generates a skybox cubemap and mounts it as the
 * visible background dome.
 *
 * Mirrors `webpages/editor/src/scene.ts: loadDefaultEnvLighting`.
 *
 * Returns the source texture so callers can dispose it if the scene is later
 * unloaded.
 */
export async function loadHdri(
    app: pc.AppBase,
    cfg: HdriConfig,
    absoluteUrl: string
): Promise<pc.Texture> {
    const device = app.graphicsDevice;
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error(`HDRI load failed: ${absoluteUrl}`));
        i.src = absoluteUrl;
    });

    const source = new pc.Texture(device, {
        name: `hdri:${cfg.url}`,
        width: img.width,
        height: img.height,
        format: pc.PIXELFORMAT_RGBA8,
        mipmaps: false,
        addressU: pc.ADDRESS_REPEAT,
        addressV: pc.ADDRESS_REPEAT
    });
    source.setSource(img);

    // IBL: generate filtered atlas. Always done — it's how meshes get lit.
    const lighting = pc.EnvLighting.generateLightingSource(source);
    const envAtlas = pc.EnvLighting.generateAtlas(lighting);
    lighting.destroy();
    app.scene.envAtlas = envAtlas;

    // Skybox: opt-in. Generates a cubemap from the same source.
    if (cfg.showSkybox) {
        app.scene.skybox = pc.EnvLighting.generateSkyboxCubemap(source);
    } else {
        app.scene.skybox = null;
    }

    return source;
}
